import { createFileRoute, Link } from "@tanstack/react-router";
import { useState } from "react";
import { MarketingShell } from "@/components/marketing-shell";
import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { PLANS, PLAN_ORDER, TRIAL_DAYS, type BillingInterval } from "@/lib/billing-plans";
import { cn } from "@/lib/utils";
import { redirectIfAuthenticated } from "@/lib/marketing-guards";

export const Route = createFileRoute("/pricing")({
  beforeLoad: redirectIfAuthenticated,
  head: () => ({
    meta: [
      { title: "Pricing, Super Affiliate OS" },
      {
        name: "description",
        content:
          "Starter $47/mo, Pro $97/mo, Super $197/mo. 14-day free trial on every plan. Save ~17% on annual billing.",
      },
      { property: "og:title", content: "Pricing, Super Affiliate OS" },
      {
        property: "og:description",
        content: "Three plans for affiliates running anything from a focused stack to an empire.",
      },
    ],
  }),
  component: PricingPage,
});

function PricingPage() {
  const [interval, setInterval] = useState<BillingInterval>("monthly");

  return (
    <MarketingShell>
      <section className="mx-auto max-w-4xl px-6 py-20 text-center">
        <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
          Simple, transparent pricing
        </h1>
        <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
          Every plan starts with a {TRIAL_DAYS}-day free trial. No charge until day {TRIAL_DAYS + 1}.
        </p>

        <div className="mx-auto mt-8 inline-flex rounded-full border border-border bg-card p-1">
          <button
            type="button"
            onClick={() => setInterval("monthly")}
            className={cn(
              "rounded-full px-4 py-1.5 text-sm font-medium transition",
              interval === "monthly" ? "bg-background shadow-sm" : "text-muted-foreground",
            )}
          >
            Monthly
          </button>
          <button
            type="button"
            onClick={() => setInterval("annual")}
            className={cn(
              "rounded-full px-4 py-1.5 text-sm font-medium transition",
              interval === "annual" ? "bg-background shadow-sm" : "text-muted-foreground",
            )}
          >
            Annual <span className="ml-1 text-xs text-success">save ~17%</span>
          </button>
        </div>
      </section>

      <section className="mx-auto grid max-w-6xl gap-6 px-6 pb-24 lg:grid-cols-3">
        {PLAN_ORDER.map((key) => {
          const def = PLANS[key];
          const price = def.display[interval];
          return (
            <div
              key={key}
              className={cn(
                "relative rounded-2xl border bg-card p-8",
                def.popular ? "border-primary shadow-lg shadow-primary/10" : "border-border",
              )}
            >
              {def.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-medium text-primary-foreground">
                  Most popular
                </div>
              )}
              <h3 className="text-lg font-semibold">{def.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{def.tagline}</p>
              <div className="mt-4 flex items-baseline gap-1">
                <span className="text-4xl font-bold">${price.amount.toLocaleString()}</span>
                <span className="text-sm text-muted-foreground">{price.suffix}</span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                {TRIAL_DAYS}-day free trial · cancel anytime
              </p>
              <ul className="mt-6 space-y-3">
                {def.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-success" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                asChild
                className="mt-8 w-full"
                variant={def.popular ? "default" : "outline"}
              >
                <Link to="/signup" search={{ plan: key, interval }}>
                  Start free trial
                </Link>
              </Button>
            </div>
          );
        })}
      </section>

      <section className="border-t border-border/60 bg-card/40">
        <div className="mx-auto max-w-3xl px-6 py-16 text-center">
          <h2 className="text-xl font-semibold">Need something custom?</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Larger teams, more workspaces, or enterprise security requirements? Let's talk.
          </p>
          <Button asChild variant="outline" className="mt-6">
            <Link to="/contact">Contact sales</Link>
          </Button>
        </div>
      </section>
    </MarketingShell>
  );
}
